import {
  addMinutes,
  differenceInDays,
  differenceInMinutes,
  format,
  getTime,
  isToday
} from "date-fns";
import {
  ClassSession,
  Course,
  IUserPurchasesResponse,
  Slot,
  SubscriptionType
} from "services/api/sessions/sessionService.types";
import _partition from "lodash.partition";
import { TrialActivationState } from "./trial-activation-modal/trial-activation-modal.types";
import { id } from "date-fns/locale";

export const getMediaURL = (baseMediaURL: string, path: string): string =>
  `${baseMediaURL}/${path}`;

export const getAllSlots = (courses: Array<Course>): Array<Slot> =>
  courses.reduce(
    (acc: Array<Slot>, course: Course) => acc.concat(course.slot),
    []
  );

export const getAllClasses = (courses: Array<Course>): Array<ClassSession> => {
  const allSlots = getAllSlots(courses);
  return allSlots.reduce(
    (acc: Array<ClassSession>, slot: Slot) => acc.concat(slot.session),
    []
  );
};

export const getOngoingAndUpcomingClasses = (
  classes: Array<ClassSession>,
  currentTimeStamp: string
): [ClassSession[], ClassSession[]] => {
  return _partition(classes, (c) =>
    isOngoingClass(c.startDate, c.endDate, currentTimeStamp)
  );
};
export const isOngoingClass = (
  startTime: string,
  endTime: string,
  currentTime: string
): boolean => {
  const startTimestamp = getModifiedStartTimeStamp(startTime, -5);
  const endTimestamp = getTimeStampFromDateString(endTime);
  const currentTimeStamp = getTimeStampFromDateString(currentTime);
  return startTimestamp <= currentTimeStamp && currentTimeStamp <= endTimestamp;
};

export const getMandatoryAndExtraClasses = (
  upComingClass: Array<ClassSession>
) => {
  return _partition(upComingClass, (c) => !c.isOptional).map(
    (upcomingClassSession) =>
      upcomingClassSession.sort(
        (a, b) =>
          getTimeStampFromDateString(a.startDate) -
          getTimeStampFromDateString(b.startDate)
      )
  );
};

export const getModifiedStartTimeStamp = (
  dateString: string,
  amountInMinutes: number
): number => {
  const startDate = getTimeStampFromDateString(dateString);
  return getTime(addMinutes(startDate, amountInMinutes));
};

export const getTimeStampFromDateString = (dateString: string): number =>
  getTime(Date.parse(dateString));

export const formatTimeStamp = (dateString: string): string => {
  const timeStampFromDateString = getTimeStampFromDateString(dateString);
  return (
    format(timeStampFromDateString, "EEE, ", { locale: id }) +
    format(timeStampFromDateString, "dd LLL")
  );
};

export const getClassTimeStamp = (startDate: string): string => {
  const isScheduledForToday = isToday(new Date(startDate));
  return isScheduledForToday ? "Hari Ini" : formatTimeStamp(startDate);
};

export const getMinutesElapsed = (
  classStartDate: string,
  currentTimeStamp: string
): number => {
  return differenceInMinutes(
    getTimeStampFromDateString(classStartDate),
    getTimeStampFromDateString(currentTimeStamp)
  );
};

export const getClassProgressPercentage = (
  elapsedTime: number,
  totalDuration: number
) =>
  parseFloat(
    (((totalDuration - elapsedTime) / totalDuration) * 100).toFixed(2)
  );

export const getTrialActivationState = (
  trialEndDate: string,
  subscriptionType: SubscriptionType | null
): TrialActivationState => {
  const trialEndTimeStamp = getTimeStampFromDateString(trialEndDate);

  if (
    subscriptionType === SubscriptionType.FREE &&
    trialEndTimeStamp >= Date.now()
  ) {
    return TrialActivationState.TRIAL_ACTIVATED;
  } else if (trialEndTimeStamp >= Date.now()) {
    return TrialActivationState.TRIAL_ACTIVE;
  } else {
    return TrialActivationState.TRIAL_EXPIRED;
  }
};

export const getVideoMaterialCountForClass = (classSession: ClassSession) => {
  const conceptVideosCount = classSession.conceptVideos
    ? classSession.conceptVideos.length
    : 0;
  const overViewVideoCount = classSession.overviewVideo ? 1 : 0;
  return conceptVideosCount + overViewVideoCount;
};

export const getVideoMaterialsForClass = (classSession: ClassSession) => {
  const conceptVideos =
    classSession.conceptVideos && classSession.conceptVideos.length > 0
      ? classSession.conceptVideos
      : [];
  const overviewVideo = classSession.overviewVideo
    ? [classSession.overviewVideo]
    : [];
  return [...overviewVideo, ...conceptVideos];
};

export const didStudentAttendClass = (classSession: ClassSession) => {
  return classSession &&
    classSession.participant &&
    classSession.participant.length > 0
    ? true
    : false;
};

export const getNumberOfPaidDaysLeft = (
  purchases: IUserPurchasesResponse[]
) => {
  const lastDateOfExpiry = purchases.reduce(
    (acc: number, purchase: IUserPurchasesResponse) => {
      if (acc === 0) {
        acc = getTimeStampFromDateString(purchase.tenure_ends_at);
      } else {
        acc =
          acc > getTimeStampFromDateString(purchase.tenure_ends_at)
            ? acc
            : getTimeStampFromDateString(purchase.tenure_ends_at);
      }
      return acc;
    },
    0
  );
  const today = new Date();
  const paidDaysLeft = differenceInDays(lastDateOfExpiry, today);
  return paidDaysLeft;
};

export const getGuruJuaraDetailsPage = (guruJuaraName: string) => {
  return `/bimbel/guru-juara/${guruJuaraName
    .split(" ")
    .join("-")
    .toLowerCase()}/`;
};

export const getClassType = (
  classSession: ClassSession,
  slotOptInDate: string,
  pastSessions: ClassSession[]
) => {
  const pastMandatorySessions = pastSessions.filter(
    (session) => session.isOptional === false
  );

  const pastNonMandatorySessions = pastSessions.filter(
    (session) => session.isOptional === true
  );

  const pastMandatoryFirstClassAfterSlotOptInDate = pastMandatorySessions?.find(
    (session) => session.startDate > new Date(slotOptInDate).toISOString()
  );

  const pastNonMandatoryFirstClassAfterSlotOptInDate =
    pastNonMandatorySessions?.find(
      (session) => session.startDate > new Date(slotOptInDate).toISOString()
    );

  if (pastMandatoryFirstClassAfterSlotOptInDate) {
    if (classSession.isOptional) {
      return "NEXT_NON_MANDATORY";
    } else {
      return "NEXT_MANDATORY";
    }
  } else if (!classSession.isOptional) {
    return "FIRST_MANDATORY";
  } else if (pastNonMandatoryFirstClassAfterSlotOptInDate) {
    return "NEXT_NON_MANDATORY";
  } else {
    const sessionStartInJakarta = new Date(
      classSession.startDate
    ).toLocaleString("en-CA", {
      timeZone: "Asia/Jakarta"
    });

    const slotOptInInJakarta = new Date(slotOptInDate).toLocaleString("en-CA", {
      timeZone: "Asia/Jakarta"
    });

    const sessionStartDate = sessionStartInJakarta.split(",")[0];
    const slotOptInDateFormatted = slotOptInInJakarta.split(",")[0];

    const isSameDay = sessionStartDate === slotOptInDateFormatted;

    if (isSameDay) {
      return "FIRST_NON_MANDATORY";
    }
    return "NEXT_NON_MANDATORY";
  }
};

export const getClassCardTitle = (classType: string, isOngoing: boolean) => {
  if (isOngoing) {
    switch (classType) {
      case "FIRST_MANDATORY":
        return "Kelas pertamamu";
      case "FIRST_NON_MANDATORY":
        return "Kelas Extra";
      case "NEXT_MANDATORY":
        return "Kelasmu lagi berlangsung";
      case "NEXT_NON_MANDATORY":
        return "Kelas extra lagi berlangsung";
      default:
        return "";
    }
  } else {
    switch (classType) {
      case "FIRST_MANDATORY":
        return "Kelas pertamamu";
      case "FIRST_NON_MANDATORY":
        return "Kelas Extra";
      case "NEXT_MANDATORY":
        return "Ini kelasmu selanjutnya";
      case "NEXT_NON_MANDATORY":
        return "Ini kelas extra selanjutnya";
      default:
        return "";
    }
  }
};
